/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : NotificationIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { componentStyle } from "../../../config/appTheme";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import notificationsAction from "../../../redux/actions/notification";
import { HandleApiActions } from "../../../redux/actions";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import CustomFilterV2 from "../../../components/customComponentsV2/common/filter/CustomFilterV2";
import SortTemplate from "../../../components/customComponentsV2/common/Templates/SortTemplate";
import FilterTemplate from "../../../components/customComponentsV2/common/Templates/FilterTemplate";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const toTitleCase = (str) =>
  str?.replaceAll("_", " ")?.replaceAll(/\b\w/g, (l) => l.toUpperCase());

const renderVfIds = (key, value) => (
  <Box key={key}>
    {toTitleCase(key)}:
    <Box sx={{ pl: 2 }}>
      {Array.isArray(value) && value.length > 0 ? (
        <Box>{value.join(", ")}</Box>
      ) : (
        <Box>-</Box>
      )}
    </Box>
  </Box>
);

const renderDeliveryMarket = (key, value) => (
  <Box key={key}>
    {toTitleCase(key)}:
    <Box sx={{ pl: 2 }}>
      {Array.isArray(value) && value.length > 0 ? (
        <Box sx={{ whiteSpace: "pre-line" }}>{value.join("\n")}</Box>
      ) : (
        <Box>-</Box>
      )}
    </Box>
  </Box>
);

const renderArrayValue = (value) =>
  value.length > 0 ? (
    value.map((item, index) => (
      <Box key={item?.id || index}>
        {Object.entries(item).map(([subKey, subValue]) => (
          <Box key={subKey}>
            {toTitleCase(subKey)}: {subValue ? String(subValue) : "-"}
          </Box>
        ))}
      </Box>
    ))
  ) : (
    <Box>-</Box>
  );

const renderObjectValue = (value) =>
  value && Object.keys(value).length > 0 ? (
    Object.entries(value).map(([subKey, subValue]) => (
      <Box key={subKey}>
        {toTitleCase(subKey)}: {subValue ? String(subValue) : "-"}
      </Box>
    ))
  ) : (
    <Box>-</Box>
  );

const renderObjectOrArray = (key, value) => (
  <Box key={key}>
    {toTitleCase(key)}:
    <Box sx={{ pl: 2 }}>
      {Array.isArray(value) ? renderArrayValue(value) : renderObjectValue(value)}
    </Box>
  </Box>
);

const renderPrimitive = (key, value) => (
  <Box key={key}>
    {toTitleCase(key)}: {value ? String(value) : "-"}
  </Box>
);

const renderModuleEntry = ([key, value]) => {
  if (key === "vf_ids") return renderVfIds(key, value);
  if (key === "delivery_market") return renderDeliveryMarket(key, value);
  if (typeof value === "object" && value !== null)
    return renderObjectOrArray(key, value);
  return renderPrimitive(key, value);
};

const ModuleDetailsPanel = ({ moduleDetails }) => (
  <Box
    sx={{
      textAlign: "left",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "auto",
    }}
  >
    {moduleDetails && Object.keys(moduleDetails).length > 0 ? (
      Object.entries(moduleDetails).map(renderModuleEntry)
    ) : (
      <Typography variant="body2">No module changes available</Typography>
    )}
  </Box>
);

ModuleDetailsPanel.propTypes = {
  moduleDetails: PropTypes.object,
};

function NotificationIndex() {
  const filterActionData = [
    { value: "read", label: "Read" },
    { value: "unread", label: "Unread" },
  ];

  const moduleType = {
    100: "Users",
    200: "Settings",
    300: "Legal",
    400: "Project",
    500: "Zone",
    600: "EE Component",
    700: "Software Module",
    800: "Tickets",
  };

  const moduleTypeForFilter = Object.entries(moduleType).map(
    ([key, value]) => ({
      value: key,
      label: value,
    }),
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profileDetails = useSelector((store) => store.profileReducer);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [recentNotificationDetails, setRecentNotificationDetails] = useState(
    [],
  );
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [anchorNotificationFilter, setAnchorNotificationFilter] =
    useState(null);
  const [openNotificationFilter, setOpenNotificationFilter] = useState(false);
  const [selectedActionType, setSelectedActionType] =
    useState(filterActionData);
  const [selectedSortObj, setSelectedSortObj] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [selectedActionTypeBeforeClick, setSelectedActionTypeBeforeClick] =
    useState(filterActionData);
  const [selectedSortObjBeforeClick, setSelectedSortObjBeforeClick] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
  });
  const [expandedRows, setExpandedRows] = useState({});

  const [selectedModuleType, setSelectedModuleType] =
    useState(moduleTypeForFilter);
  const [selectedModuleTypeBeforeClick, setSelectedModuleTypeBeforeClick] =
    useState(moduleTypeForFilter);

  const getModuleType = (statusCode) => {
    return moduleType[statusCode] || "-";
  };

  const handleMarkAsRead = async (notificationId) => {
    if (!notificationId) {
      return;
    }

    const notification = (recentNotificationDetails || []).find(
      (n) => n.notification_id === notificationId,
    );
    if (notification?.status) {
      return; // Exit if already read
    }

    try {
      const apiDetails = notificationsAction.mark_read_notifications;
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              notification_ids: [notificationId],
            },
            show_toast: false,
          }),
        ),
      );

      if (!resp?.error) {
        setRecentNotificationDetails((prev) =>
          (prev || []).map((notificationItem) =>
            notificationItem.notification_id === notificationId
              ? { ...notificationItem, status: true }
              : notificationItem,
          ),
        );
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = (recentNotificationDetails || []).filter(
      (notification) => !notification.status,
    );

    if (unreadNotifications.length === 0) {
      return; // Exit if there are no unread notifications
    }

    try {
      const apiDetails = notificationsAction.mark_read_notifications;

      const notificationIds = unreadNotifications.map(
        (notification) => notification.notification_id,
      );

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              notification_ids: notificationIds,
            },
            show_toast: true,
          }),
        ),
      );

      if (!resp?.error) {
        setRecentNotificationDetails(
          (prev) =>
            (prev || []).map((notification) => ({ ...notification, status: true })),
        );
      }
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const fetchNotifications = async () => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...notificationsAction?.get_all_notifications,
          params: {
            page_number: page,
            page_size: rowsPerPage,
            ...(selectedModuleType?.length && {
              module_type: selectedModuleType.map((e) => e.value).join(","),
            }),
            ...(selectedActionType?.length !== 2 && {
              status: selectedActionType[0]?.value === "read",
            }),
            ...(selectedSortObj.sortBy === "created_at" && {
              is_old_to_new: selectedSortObj.sortOrder === "ASC",
            }),
            ...(selectedSortObj.sortBy === "updated_at" && {
              sort_updated_at: selectedSortObj.sortOrder,
            }),
          },
          show_toast: false,
        }),
      ),
    );

    if (resp?.error) {
      setRecentNotificationDetails([]);
    } else {
      setRecentNotificationDetails(resp?.data || []);
      setTotalCount(resp?.total_count || 0);
    }
  };

  const handleNotificationFilterIconClick = (event) => {
    setAnchorNotificationFilter(event.currentTarget);
    setOpenNotificationFilter(true);
  };

  const handleNotificationFilterIconClose = () => {
    setAnchorNotificationFilter(null);
    setOpenNotificationFilter(false);
  };

  const handleApplyFilters = async (action) => {
    switch (action) {
      case "discard":
        setSelectedActionTypeBeforeClick(filterActionData);
        setSelectedModuleTypeBeforeClick(moduleTypeForFilter);
        setSelectedSortObjBeforeClick({
          sortBy: "created_at",
          sortOrder: "DESC",
        });
        break;

      case "cancel":
        handleNotificationFilterIconClose();
        break;

      case "apply": {
        const newSortObj = selectedSortObjBeforeClick;
        const newActionType = selectedActionTypeBeforeClick;
        const newModuleType = selectedModuleTypeBeforeClick;

        setSelectedSortObj(newSortObj);
        setSelectedActionType(newActionType);
        setSelectedModuleType(newModuleType);
        setPage(1);

        const resp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...notificationsAction?.get_all_notifications,
              params: {
                page_number: 1,
                page_size: rowsPerPage,
                ...(newModuleType?.length && {
                  module_type: newModuleType.map((e) => e.value).join(","),
                }),
                ...(newActionType?.length !== 2 && {
                  status: newActionType[0]?.value === "read",
                }),
                ...(newSortObj.sortBy === "created_at" && {
                  is_old_to_new: newSortObj.sortOrder === "ASC",
                }),
                ...(newSortObj.sortBy === "updated_at" && {
                  sort_updated_at: newSortObj.sortOrder,
                }),
              },
              show_toast: false,
            }),
          ),
        );

        if (resp?.error) {
          setRecentNotificationDetails([]);
        } else {
          setRecentNotificationDetails(resp?.data || []);
          setTotalCount(resp?.total_count || 0);
        }

        handleNotificationFilterIconClose();
        break;
      }

      default:
        break;
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const filteredNotificationList = recentNotificationDetails?.map(
      (item, index) => ({
        ...item,
        id: (page - 1) * rowsPerPage + index + 1,
        sno: (page - 1) * rowsPerPage + index + 1,
        isRead: item?.status,
        module_type: item?.module_type,
        module_details: item?.module_details,
        header: item?.header,
        message: item?.message,
        created_at: convertUtcToTimeZone(
          item?.created_at,
          profileDetails?.profile?.profile?.region,
        ),
        updated_at: convertUtcToTimeZone(
          item?.updated_at,
          profileDetails?.profile?.profile?.region,
        ),
        status: item?.status,
        allData: item,
      }),
    );

    setTableRowsData(filteredNotificationList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentNotificationDetails]);

  const handleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const generateTableRows = () => {
    const rows = [];
    for (const [index, item] of (tableRowsData || []).entries()) {
      rows.push({
        ...item,
        id: item.id || index,
        isMainRow: true,
      });

      if (expandedRows[item.id]) {
        rows.push({
          id: `${item.id}-detail`,
          isDetailRow: true,
          parentData: item,
        });
      }
    }
    return rows;
  };

  const COLUMNS = [
    {
      field: "expand",
      headerName: "",
      flex: 0.2,
      minWidth: 40,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isDetailRow) return null;

        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowExpansion(params.row.id);
            }}
            sx={{ p: 0.5 }}
          >
            {expandedRows[params.row.id] ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      field: "module_type",
      headerName: "Module",
      headerAlign: "center",
      flex: 1,
      colSpan: (params) => {
        return params?.row?.isDetailRow ? 4 : 1;
      },
      renderCell: (params) => {
        if (params.row.isDetailRow) {
          return (
            <Box
              sx={{
                p: 2,
                width: "100%",
                height: "100%",
              }}
            >
              <ModuleDetailsPanel
                moduleDetails={params.row.parentData.module_details}
              />
            </Box>
          );
        }
        return (
          <Box sx={{ textAlign: "center" }}>{getModuleType(params.value)}</Box>
        );
      },
    },
    {
      field: "header",
      headerName: "Header",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => {
        if (params.row.isDetailRow) return <div style={{ display: "none" }} />;
        return <Box sx={{ textAlign: "left" }}>{params.value || "-"}</Box>;
      },
    },
    {
      field: "message",
      headerName: "Message",
      headerAlign: "center",
      flex: 2,
      renderCell: (params) => {
        if (params.row.isDetailRow) return <div style={{ display: "none" }} />;
        return <Box sx={{ textAlign: "left" }}>{params?.value || "-"}</Box>;
      },
    },
    {
      field: "created_at",
      headerName: "Created At",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => {
        if (params.row.isDetailRow) return <div style={{ display: "none" }} />;
        return <Box sx={{ textAlign: "center" }}>{params?.value || "-"}</Box>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      headerAlign: "center",
      flex: 1,
      renderCell: (params) => {
        if (params.row.isDetailRow) return <div style={{ display: "none" }} />;
        return (
          <Box sx={{ textAlign: "center" }}>
            <Box>
              <CustomButtonV2
                btnName={params.row.status ? "Read" : "Mark as read"}
                type={"other"}
                handleOnClick={() =>
                  handleMarkAsRead(params.row.notification_id)
                }
              />
            </Box>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Grid container>
        <Grid
          item
          sm={12}
          sx={{
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <Typography sx={{ ...componentStyle.font.tabLevelHeader }}>
            Notification
          </Typography>
        </Grid>
        <Grid
          item
          sm={12}
          sx={{
            mt: 2,
            display: "flex",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box>
              <CustomButtonV2
                startIcon={<ArrowBackIcon />}
                btnName={"Back"}
                handleOnClick={() => {
                  navigate(-1);
                }}
              />
            </Box>
            <Box>
              <CustomFilterV2
                anchorEl={anchorNotificationFilter}
                open={openNotificationFilter}
                widthPerColumn="250px"
                handleClick={handleNotificationFilterIconClick}
                handleClose={handleNotificationFilterIconClose}
                handleFilterButton={handleApplyFilters}
                showClearAll={true}
                handleClearAll={() => handleApplyFilters("discard")}
                sections={[
                  {
                    heading: "Sorting",
                    content: (
                      <SortTemplate
                        defaultValue={selectedSortObjBeforeClick}
                        handleOnChange={(sortValue) => {
                          setSelectedSortObjBeforeClick(sortValue);
                        }}
                      />
                    ),
                  },
                  {
                    heading: "Filter By Action",
                    content: (
                      <FilterTemplate
                        data={filterActionData}
                        handleOnChange={(value) => {
                          setSelectedActionTypeBeforeClick(value);
                        }}
                        defaultValue={selectedActionTypeBeforeClick}
                      />
                    ),
                  },
                  {
                    heading: "Filter By Module",
                    content: (
                      <FilterTemplate
                        data={moduleTypeForFilter}
                        handleOnChange={(value) => {
                          setSelectedModuleTypeBeforeClick(value);
                        }}
                        defaultValue={selectedModuleTypeBeforeClick}
                      />
                    ),
                  },
                ]}
              />
            </Box>
          </Box>
          <Box>
            <CustomButtonV2
              handleOnClick={handleMarkAllAsRead}
              btnName={"Mark All Read"}
              type="other"
            />
          </Box>
        </Grid>
        <Grid item sm={12} sx={{ mt: 1, p: 2 }}>
          <Box sx={{ height: "500px", overflow: "auto" }}>
            <CustomDataGridV2
              columns={COLUMNS}
              rows={generateTableRows()}
              page={page}
              isMultiLine={true}
              rowsPerPage={rowsPerPage}
              handlePageChange={(p) => {
                setPage(p);
                fetchNotifications();
              }}
              totalItems={totalCount}
              showPagination={true}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default NotificationIndex;
